import { Box } from "@mui/material";
import { NavbarFilters, useToast, type FilterMenuItem } from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { createProductSearchOptions } from "~/config/searchProductConfig";
import NavSearchInput from "~/features/navbar/NavSearchInput";
import { useGetMultiTempGridInfo } from "~/hooks/useGetMultiTempGridInfo";
import { useKeyDownHandler, useBarcodeScanner } from "~/lib/barCodeScan";
import { countByFilter } from "~/lib/filterCounts";
import { searchProduct } from "~/lib/helpers";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { parseProductTempZone, TempZone } from "~/lib/tempZone";
import { nonNullable } from "~/lib/typeHelpers";
import { selectUsersClientId } from "~/redux/selectors/authSelectors";
import { selectWorkstationAutostoreGridId } from "~/redux/selectors/workstationsSelectors";
import { useGetCycleCountFilterCountsQuery } from "~/redux/warehouse/cycleCountsOverhaul.hooks";

import { ProductResult } from "./ProductResult";
import {
  setPage,
  setScanState,
  setSearchedVariant,
  setScannedBarcode,
  setTempZoneFilters
} from "./cycleCountsNew.slice";

export function CycleCountsSearch() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { warningToast } = useToast();
  const { isMultiTempGrid, tempZones } = useGetMultiTempGridInfo();

  const clientId = useAppSelector(selectUsersClientId);
  const gridId = useAppSelector(selectWorkstationAutostoreGridId);
  const searchedVariant = useAppSelector(
    (state) => state.cycleCountsNewSlice.searchedVariant
  );
  const selectedTempZoneFilters = useAppSelector(
    (state) => state.cycleCountsNewSlice.tempZoneFilters
  );
  const trackedPageName = useMixpanelPageName();

  const shouldFetchFilterCounts =
    isMultiTempGrid && !!tempZones.length && !!gridId;
  const { tempZoneCounts, error: filterCountsError } =
    useGetCycleCountFilterCountsQuery(
      shouldFetchFilterCounts
        ? {
            gridId,
            variantId: searchedVariant?.variantId
          }
        : skipToken,
      {
        refetchOnMountOrArgChange: true,
        selectFromResult: (result) => ({
          ...result,
          tempZoneCounts: countByFilter(
            result.data?.temperatureZoneFilterCounts
          )
        })
      }
    );

  useEffect(() => {
    if (filterCountsError) {
      warningToast(getMessageFromRtkError(filterCountsError));
    }
  }, [filterCountsError, warningToast]);

  useKeyDownHandler();

  const [autocompleteOptions, updateAutocompleteOptions] = useState<
    ProductResult[]
  >([] as ProductResult[]);
  const [searchText, setSearchText] = useState<string | null>(null);

  const tempZoneMenuItems = tempZones
    .map(parseProductTempZone)
    .filter(nonNullable)
    .map<FilterMenuItem>((zone) => ({
      translation: t(zone.toLowerCase() as TempZone),
      filter: zone,
      count: tempZoneCounts.get(zone.toLowerCase())
    }));

  const autocompleteProductSearch = async (
    input: string | null
  ): Promise<void> => {
    if (input === null || input === "" || !clientId) {
      updateAutocompleteOptions([]);
    } else {
      // fetch variants to get variantId to index counting-task as inventory or variant data doesn't exist in the task data
      const hits = await searchProduct(input);

      const hitsAsAutocompleteRecords = createProductSearchOptions({ hits });

      updateAutocompleteOptions([...hitsAsAutocompleteRecords]);
    }
  };

  useBarcodeScanner<string>({
    findScanMatch: (buffer: string) => {
      dispatch(setScannedBarcode(buffer));
      return buffer;
    },
    processScanMatch: async (buffer: string | null) => {
      if (!buffer) {
        dispatch(setScanState("error"));
        return;
      }
      const hits = await searchProduct(buffer);
      const hitsAsAutocompleteRecords = createProductSearchOptions({ hits });
      if (hitsAsAutocompleteRecords.length === 0) {
        dispatch(setScanState("error"));
        return;
      }
      if (hitsAsAutocompleteRecords.length === 1) {
        dispatch(setScanState("success"));
        dispatch(setPage(1));
        dispatch(setSearchedVariant(hitsAsAutocompleteRecords[0]));
      }
    },
    deps: []
  });

  return (
    <Box
      id="autostorePutaway-navbar-searchComponent"
      display="flex"
      flexGrow="1"
      justifyContent="center"
    >
      <NavSearchInput<ProductResult>
        textInput={searchText || ""}
        setTextInput={(text) => setSearchText(text)}
        isAutocomplete
        searchPlaceholder={t("search cycle counts on inventory")}
        autocompleteSearchCb={async (input) => {
          await autocompleteProductSearch(input);
        }}
        autocompleteOptions={autocompleteOptions}
        autocompleteLabelConstructor={(option: ProductResult): string =>
          option.displayText
        }
        groupBy={(option) => t(option.type as "inventory" | "bin")}
        selectCb={(result: ProductResult) => {
          dispatch(setPage(1));
          dispatch(setSearchedVariant(result));
        }}
        clearTextOnSelect
      />
      {isMultiTempGrid && !!tempZoneMenuItems.length && (
        <NavbarFilters
          menuItemList={tempZoneMenuItems}
          menuItemsTitle={t("temperature")}
          selectedMenuItems={selectedTempZoneFilters}
          onFilterData={(filters) => {
            dispatch(
              setTempZoneFilters(
                filters.map(parseProductTempZone).filter(nonNullable)
              )
            );
            dispatch(setPage(1));
          }}
          hasActiveFilters={selectedTempZoneFilters.length > 0}
          onFiltersMenuClose={() => {
            if (selectedTempZoneFilters.length > 0) {
              mixpanelTrack({
                trackedPageName,
                type: "Input Change",
                label: "Cycle count temp zone filter selection",
                eventProperties: {
                  "Filter count": selectedTempZoneFilters.length,
                  Filters: selectedTempZoneFilters
                }
              });
            }
          }}
          onResetFilters={() => {
            mixpanelTrack({
              trackedPageName,
              type: "Button Click",
              label: "Reset cycle count filters"
            });
            dispatch(setTempZoneFilters([]));
            dispatch(setPage(1));
          }}
        />
      )}
    </Box>
  );
}
